import { useState, useCallback } from 'react';
import { DndContext, type DragEndEvent, type DragStartEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { FormType, FormField } from '@/types/form';
import { formStorage } from '@/utils/form-storage';
import { Button } from '@/components/ui/button';
import { Save, Eye, Settings, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { FieldPalette } from './FieldPalette';
import { FormCanvas } from './FormCanvas';
import { FieldEditor } from './FieldEditor';
import { FormSettings } from './FormSettings';
import { FormPreview } from './FormPreview';

interface FormBuilderProps {
  formId?: string;
  onSave?: (form: FormType) => void;
}

export const FormBuilder: React.FC<FormBuilderProps> = ({ formId, onSave }) => {
  const navigate = useNavigate();
  
  const [formType, setFormType] = useState<FormType>(() => {
    if (formId) {
      const existing = formStorage.getFormById(formId);
      if (existing) return existing;
    }
    
    // Create new form
    const now = new Date().toISOString();
    return {
      id: 'form-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
      name: 'New Form',
      description: 'Form description',
      category: 'other',
      version: '1.0',
      status: 'draft',
      fields: [],
      metadata: {
        createdAt: now,
        updatedAt: now,
        tags: [],
        settings: {
          allowMultipleSubmissions: true,
          requireAuthentication: false,
          enableDrafts: true,
          notifications: {
            onSubmit: false,
            recipients: [],
          },
        },
      },
    };
  });

  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [draggedFieldId, setDraggedFieldId] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const selectedField = selectedFieldId ? formType.fields.find(f => f.id === selectedFieldId) : null;

  const handleSave = useCallback(async () => {
    try {
      formStorage.saveForm(formType);
      setIsDirty(false);
      toast.success('Form saved successfully');
      onSave?.(formType);
    } catch (error) {
      console.error('Failed to save form:', error);
      toast.error('Failed to save form');
    }
  }, [formType, onSave]);

  const handleAddField = useCallback((field: Omit<FormField, 'id' | 'sortOrder'>) => {
    const newField: FormField = {
      ...field,
      id: 'field-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
      sortOrder: formType.fields.length,
    };

    setFormType(prev => ({
      ...prev,
      fields: [...prev.fields, newField],
    }));
    setSelectedFieldId(newField.id);
    setIsDirty(true);
  }, [formType.fields.length]);

  const handleUpdateField = useCallback((fieldId: string, updates: Partial<FormField>) => {
    setFormType(prev => ({
      ...prev,
      fields: prev.fields.map(field => 
        field.id === fieldId ? { ...field, ...updates } : field
      ),
    }));
    setIsDirty(true);
  }, []);

  const handleDeleteField = useCallback((fieldId: string) => {
    setFormType(prev => ({
      ...prev,
      fields: prev.fields
        .filter(field => field.id !== fieldId)
        .map((field, index) => ({ ...field, sortOrder: index })),
    }));
    
    if (selectedFieldId === fieldId) {
      setSelectedFieldId(null);
    }
    setIsDirty(true);
  }, [selectedFieldId]);

  const handleDuplicateField = useCallback((fieldId: string) => {
    const field = formType.fields.find(f => f.id === fieldId);
    if (!field) return;

    const newField: FormField = {
      ...field,
      id: 'field-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
      name: field.name + '_copy',
      label: field.label + ' (Copy)',
      sortOrder: formType.fields.length,
    };

    setFormType(prev => ({
      ...prev,
      fields: [...prev.fields, newField],
    }));
    setSelectedFieldId(newField.id);
    setIsDirty(true);
  }, [formType.fields]);

  const handleDragStart = (event: DragStartEvent) => {
    setDraggedFieldId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setDraggedFieldId(null);

    if (!over || active.id === over.id) return;

    const oldIndex = formType.fields.findIndex(field => field.id === active.id);
    const newIndex = formType.fields.findIndex(field => field.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newFields = arrayMove(formType.fields, oldIndex, newIndex).map((field, index) => ({
        ...field,
        sortOrder: index,
      }));

      setFormType(prev => ({ ...prev, fields: newFields }));
      setIsDirty(true);
    }
  };

  const handleUpdateForm = useCallback((updates: Partial<FormType>) => {
    setFormType(prev => ({ ...prev, ...updates }));
    setIsDirty(true);
  }, []);

  if (showPreview) {
    return (
      <FormPreview
        form={formType}
        onClose={() => setShowPreview(false)}
        onSave={handleSave}
      />
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/forms')}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold">{formType.name}</h1>
              <p className="text-sm text-muted-foreground">
                {formType.fields.length} fields • {formType.status}
              </p>
            </div>
            {isDirty && (
              <span className="text-sm text-amber-600 bg-amber-100 px-2 py-1 rounded">
                Unsaved changes
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSettings(true)}
            >
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPreview(true)}
            >
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
            <Button size="sm" onClick={handleSave} disabled={!isDirty}>
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Field Palette */}
        <div className="w-80 border-r border-border bg-card overflow-y-auto">
          <FieldPalette onAddField={handleAddField} />
        </div>

        {/* Form Canvas */}
        <div className="flex-1 overflow-y-auto">
          <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <SortableContext items={formType.fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
              <FormCanvas
                form={formType}
                selectedFieldId={selectedFieldId}
                draggedFieldId={draggedFieldId}
                onSelectField={setSelectedFieldId}
                onUpdateField={handleUpdateField}
                onDeleteField={handleDeleteField}
                onDuplicateField={handleDuplicateField}
              />
            </SortableContext>
          </DndContext>
        </div>

        {/* Field Editor */}
        {selectedField && (
          <div className="w-96 border-l border-border bg-card overflow-y-auto">
            <FieldEditor
              field={selectedField}
              onUpdateField={(updates) => handleUpdateField(selectedField.id, updates)}
              onClose={() => setSelectedFieldId(null)}
            />
          </div>
        )}
      </div>

      {/* Form Settings Modal */}
      {showSettings && (
        <FormSettings
          form={formType}
          onSave={handleUpdateForm}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
};